<template>
  <div>
    <loader v-if="getIsProjectLoading" :message="message" />
    <div
      class="main_wrapper"
      :style="getShowPastDueNotification ? 'top: 40px;' : ''"
    >
      <Navigation />
      <div class="sideandboard">
        <sidebar-section v-if="getIsProjectLoaded" />
        <board-section v-if="getIsProjectLoaded" ref="board" />
        <search-section v-show="getSearchSection" />
        <Protect
          :permissions="[SYS_PERMISSIONS.SYS_PROJECTS_COMMENT]"
          :roles="['editor', 'viewer']"
        >
          <comments-section v-show="getCommentSection" />
        </Protect>
        <asset-file-uploader v-if="getIsLoggedIn" id="file-uploader" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import emitter from 'tiny-emitter/instance';
import hljs from 'highlight.js';
import Protect from '@/hoc/Protect.vue';
import { SYS_PERMISSIONS, EVENT } from '@/constants.js';
import observers from '@/queries/observers.js';
import { queries } from '@/queries/index.js';
import { useQuery } from '@tanstack/vue-query';
import { useRoute } from 'vue-router';
import updater from '../../vuex/project/updater.js';
import Loader from './Loader.vue';
import SearchSection from './SearchSection.vue';
import CommentsSection from './CommentsSection/CommentsSection.vue';
import SidebarSection from '../sidebar/SidebarSection.vue';
import BoardSection from '../board/BoardSection.vue';
import arcscript from '../../arcscript/arcscript.js';
import ArcscriptLanguagePlugin from '../../arcscript/arcscriptPlugin.js';
import AssetFileUploader from '../uploader/AssetFileUploader.vue';

import Navigation from '../navigation/Navigation.vue';

export default {
  compatConfig: {
    MODE: 2,
  },
  components: {
    loader: Loader,
    'sidebar-section': SidebarSection,
    'board-section': BoardSection,
    'search-section': SearchSection,
    'comments-section': CommentsSection,
    'asset-file-uploader': AssetFileUploader,
    Protect,
    Navigation,
  },

  /**
   * Vue router navigation guard. Called when the route that renders this component has changed, but this component is reused in the new route.
   * For example, for a route with dynamic params `/foo/:id`, when we navigate between `/foo/1` and `/foo/2`, the same `Foo` component instance
   * will be reused, and this hook will be called when that happens. Has access to `this` component instance.
   */
  beforeRouteUpdate(to, from, next) {
    // If user changed the param update the component
    this.showProject(to.params.hash);
    next();
  },

  setup() {
    const route = useRoute();
    // Used to update the state with the project permissions
    const { data: permissions } = useQuery(
      queries.permissions.project(route.params.hash)
    );

    const { data: features } = useQuery(
      queries.features.project(route.params.hash)
    );

    return { permissions, features };
  },

  data() {
    return {
      hash: null,
      message: 'Loading project...',
      SYS_PERMISSIONS,
      // pressedKeys: {},
    };
  },

  head() {
    const cover = this.getCoverUrl;
    const metaHead = {
      title: this.getName,
      meta: [
        {
          name: 'og:title',
          property: 'og:title',
          content: this.getName,
        },
      ],
    };

    if (cover) {
      metaHead.meta.push(
        {
          name: 'og:image',
          property: 'og:image',
          content: cover,
        },
        {
          name: 'twitter:image',
          property: 'twitter:image',
          content: cover,
        }
      );
    }
    return metaHead;
  },

  computed: {
    ...mapGetters('application/appStates', [
      'getIsProjectLoading',
      'getIsProjectLoaded',
    ]),
    ...mapGetters('application/modals', ['isModalOpen']),
    ...mapGetters('project/config/components', ['getOpen']),
    ...mapGetters('project', [
      'getHash',
      'getPublicLink',
      'getName',
      'getCoverUrl',
    ]),
    ...mapGetters('project/config', ['getGrid']),
    ...mapGetters('project/users', ['amIParticipant']),
    ...mapGetters('user/account', ['getIsLoggedIn']),
    ...mapGetters('application', ['getSearchSection', 'getCommentSection']),
    ...mapGetters('project/data/variables', ['getNames']),
    ...mapGetters('workspace', ['getShowPastDueNotification']),
  },

  watch: {
    /**
     * Watch public link property change.
     */
    getPublicLink(newValue) {
      this.leaveProjectChannels({ hash: this.getHash });
      this.startProjectChannels();
      // Check authorization
      if (!this.amIParticipant && !newValue) {
        this.$router.replace('/project-error/403');
      }
    },

    permissions(newValue) {
      if (
        !newValue ||
        !newValue.permissions ||
        newValue.permissions.length === 0
      ) {
        this.$router.replace('/project-error/403');
      }
    },
  },

  /**
   * Created lifecycle hook.
   */
  created() {
    const { hash } = this.$route.params;
    this.hash = hash;
    // Load the project
    this.showProject(hash);

    hljs.registerLanguage(
      'arcscript',
      arcscript(() => this.getNames)
    );
    hljs.configure({ languages: ['arcscript'] });
    hljs.addPlugin(new ArcscriptLanguagePlugin({ dataPrefix: 'hljs' }));

    // Listen for document wide key events
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
  },

  unmounted() {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    this.leaveProjectChannels({ hash: this.getHash });
    observers.unsubscribe('externalUsers');
  },

  methods: {
    ...mapActions(['loadProject']),
    ...mapActions('application/channels', [
      'startProjectChannels',
      'leaveProjectChannels',
    ]),
    ...mapActions('application', ['setSearchSection']),

    /**
     * Event handler for window keydown event
     * @param {KeyboardEvent} event
     */
    onKeyDown(event) {
      const { key, ctrlKey, metaKey, shiftKey } = event;
      const { ARROW_KEY_STATE_CHANGE } = EVENT;

      // If Search do it before disabling inputs
      if ((ctrlKey || metaKey) && key === 'f') {
        event.preventDefault();
        this.setSearchSection({ value: !this.getSearchSection });
        return;
      }

      // ESC close search if open
      if (key === 'Escape' && this.getSearchSection) {
        this.setSearchSection({ value: false });
        return;
      }

      // if any input has focus or editing a component do not do anything
      if (this.isInputFocus() || this.getOpen || this.isModalOpen) {
        console.log('No handling from window');
        return;
      }

      switch (key) {
        // If user presses 'delete' or 'backspace'
        case 'Delete':
        case 'Backspace':
          event.preventDefault();
          // Allow deletetion only if there is no open component
          // if (!this.getOpen) {
          emitter.emit(EVENT.DELETE_SELECTIONS);
          // }
          break;

        // If user presses 'a'
        case 'a':
        case 'A':
          if (ctrlKey || metaKey) {
            // Do not select anything else on the page
            event.preventDefault();
            emitter.emit(EVENT.SELECT_ALL);
          }
          break;

        // If user presses 'c'
        case 'c':
        case 'C':
          if (ctrlKey || metaKey) {
            emitter.emit(EVENT.COPY);
          }
          break;

        // If user presses 'f'
        case 'f':
        case 'F':
          emitter.emit(EVENT.FIT_TO_SCREEN);
          break;

        // If user presses 'x'
        case 'x':
        case 'X':
          if (ctrlKey || metaKey) {
            emitter.emit(EVENT.CUT);
          }
          break;

        // If user presses 'v'
        case 'v':
        case 'V':
          if (ctrlKey || metaKey) {
            if (shiftKey) {
              event.preventDefault();
              emitter.emit(EVENT.SHIFT_PASTE);
            } else {
              emitter.emit(EVENT.PASTE);
            }
          }
          break;

        // If user presses 'z'
        case 'z':
        case 'Z':
          if ((ctrlKey || metaKey) && !shiftKey) {
            event.preventDefault();
            updater.undo();
          } else if ((ctrlKey || metaKey) && shiftKey) {
            event.preventDefault();
            updater.redo();
          }
          break;

        // If user presses '+'
        case '+':
          // case 107:
          emitter.emit(EVENT.ZOOMIN);
          break;

        // If user presses '-'
        case '-':
          // case 109:
          emitter.emit(EVENT.ZOOMOUT);
          break;

        // If user presses '0'
        case '0':
          emitter.emit(EVENT.RESETZOOM);
          break;

        // If user presses 'Esc'
        case 'Escape':
          emitter.emit(EVENT.ESC_BOARD);
          break;

        // One of arrow keys (reversed vector compared with onKeyDown handler)
        case 'ArrowLeft':
          console.log('Handling arrow left down');
          if (!metaKey) {
            emitter.emit(ARROW_KEY_STATE_CHANGE, { x: -1, y: 0 }, shiftKey);
          }
          break;
        case 'ArrowRight':
          if (!metaKey) {
            emitter.emit(ARROW_KEY_STATE_CHANGE, { x: 1, y: 0 }, shiftKey);
          }
          break;
        case 'ArrowUp':
          if (!metaKey) {
            emitter.emit(ARROW_KEY_STATE_CHANGE, { x: 0, y: -1 }, shiftKey);
          }
          break;
        case 'ArrowDown':
          if (!metaKey) {
            emitter.emit(ARROW_KEY_STATE_CHANGE, { x: 0, y: 1 }, shiftKey);
          }
          break;

        default:
          break;
      }
    },

    /**
     * Event handler for window keyup event
     * @param {KeyboardEvent} event
     */
    onKeyUp(event) {
      const { key, shiftKey, metaKey } = event;
      const { ARROW_KEY_STATE_CHANGE } = EVENT;

      // If any input has focus or editing a component do not do anything
      if (this.isInputFocus() || this.getOpen || this.isModalOpen) {
        return;
      }

      switch (key) {
        // One of arrow keys
        case 'ArrowLeft':
          if (!metaKey) {
            emitter.emit(ARROW_KEY_STATE_CHANGE, { x: 1, y: 0 }, shiftKey);
          }
          break;
        case 'ArrowRight':
          if (!metaKey) {
            emitter.emit(ARROW_KEY_STATE_CHANGE, { x: -1, y: 0 }, shiftKey);
          }
          break;
        case 'ArrowUp':
          if (!metaKey) {
            emitter.emit(ARROW_KEY_STATE_CHANGE, { x: 0, y: 1 }, shiftKey);
          }
          break;
        case 'ArrowDown':
          if (!metaKey) {
            emitter.emit(ARROW_KEY_STATE_CHANGE, { x: 0, y: -1 }, shiftKey);
          }
          break;
        default:
          break;
      }
    },

    /**
     * Load and display the project
     */
    showProject(hash) {
      this.loadProject({ hash })
        .then(response => {
          const isParticipant = this.amIParticipant;
          const { publicLink } = response.data;
          // Do not allow access if user is not a participant and public link is for play mode only
          if (!isParticipant && publicLink !== 'design') {
            this.$router.replace('/project-error/403');
          }

          if (isParticipant) {
            observers.create(
              'externalUsers',
              queries.project.externalUsers(hash)
            );
          }

          // Start listening to project updates - collaborator updates
          this.startProjectChannels();
        })
        .catch(error => {
          console.error('ShowProject error:', error);
          const { status } = error.response.data;
          if (status === 404) {
            this.$router.replace('/project-error/404');
          } else if (status === 403) {
            this.$router.replace('/project-error/403');
          }
        });
    },

    // Returns whether the focus is in input, content area or textarea
    isInputFocus() {
      return (
        $('input:focus').length > 0 ||
        $("div[contenteditable='true']:focus").length > 0 ||
        $('textarea:focus').length > 0
      );
    },
  },
};
</script>

<style scoped lang="scss">
.main_wrapper {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-flow: column;

  .sideandboard {
    display: flex;
    flex-flow: row;
    min-height: 0;
    position: relative;
  }
}
</style>
<style lang="scss">
.v-popper__popper {
  display: block !important;
  z-index: 200;

  .v-popper__inner {
    background: black;
    color: #bcbcbc;
    border-radius: 3px;
    padding: 5px 10px 4px;

    .shortcut {
      opacity: 0.6 !important;
      text-transform: uppercase;
      display: inline-block;
      margin: 0 0 0 5px;
      font-weight: 600;
    }
  }
  /*
  .v-popper__arrow-outer,
  .v-popper__arrow-inner {
    width: 0;
    height: 0;
    border-style: solid;
    position: absolute;
    margin: 5px;
    border-color: black;
    z-index: 1;
  }

  &[data-popper-placement^='top'] {
    margin-bottom: 10px;

    .v-popper__arrow-outer,
    .v-popper__arrow-inner {
      border-width: 5px 5px 0 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      bottom: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &[data-popper-placement^='bottom'] {
    margin-top: 10px;

    .v-popper__arrow-outer,
    .v-popper__arrow-inner {
      border-width: 0 5px 5px 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-top-color: transparent !important;
      top: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &[data-popper-placement^='right'] {
    margin-left: 10px;

    .v-popper__arrow-outer,
    .v-popper__arrow-inner {
      border-width: 5px 5px 5px 0;
      border-left-color: transparent !important;
      border-top-color: transparent !important;
      border-bottom-color: transparent !important;
      left: -5px;
      top: calc(50% - 5px);
      margin-left: 0;
      margin-right: 0;
    }
  }

  &[data-popper-placement^='left'] {
    margin-right: 5px;

    .v-popper__arrow-outer,
    .v-popper__arrow-inner {
      border-width: 5px 0 5px 5px;
      border-top-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      right: -5px;
      top: calc(50% - 5px);
      margin-left: 0;
      margin-right: 0;
    }
  }*/

  &[aria-hidden='true'] {
    visibility: hidden;
    opacity: 0;
    // transition: opacity 0.15s, visibility 0.15s;
  }

  &[aria-hidden='false'] {
    visibility: visible;
    opacity: 1;
    // transition: opacity 0.15s;
  }

  &.v-popper--theme-dropdown {
    $color: #2c3233;
    margin-right: 5px;
    max-width: calc(100% - 10px);

    .v-popper__inner {
      background: $color;
      color: #bcbcbc;
      padding: 10px;
      border-radius: 3px;
      border-width: 0;
      box-shadow: 0 1px 10px rgba(0, 0, 0, 0.5);

      &.component {
        padding: 10px;
        background: #242929;

        > div {
          display: flex;
        }
      }
    }

    &.no-padding {
      .v-popper__inner {
        padding: 0;
      }
    }

    .thumb {
      background: url(/images/component.svg?3606044…) #485050;
      background-repeat: no-repeat;
      background-size: 44px;
      background-position: center;
      border-radius: 3px;
      max-width: 100px;
      width: 100px;
      max-height: 100px;
      height: 100px;
      overflow: hidden;
      margin: 0 10px 0 0;

      &.nobackground {
        background: #485050;
      }

      img {
        object-fit: cover;
        width: 100%;
        height: 100%;
      }
    }

    .name {
      padding: 0 0 5px 0;
      color: #fff;
      font-size: 16px;
      font-weight: 100;
    }

    small {
      text-transform: uppercase;
      display: block;
      padding: 0;
      opacity: 0.5;

      &.bottom {
        padding: 0;
      }
    }

    .boardname {
      padding: 5px 0;
      color: #fff;
      font-size: 16px;
      font-weight: 100;
    }

    .v-popper__arrow-outer,
    .v-popper__arrow-inner {
      border-color: $color;

      &.component {
        border-color: #242929;
      }
    }
  }
}

.slide-fade-enter {
  transform: translateX(100%);
  // opacity: 0;
}

.slide-fade-enter-to {
  transform: translateX(0);
  // opacity: 1;
}

.slide-fade-leave {
  // opacity: 1;
  transform: translateX(0);
}
.slide-fade-leave-to {
  transform: translateX(100%);
  // opacity: 0;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.2s ease;
  position: absolute;
  top: 0px !important;
  bottom: 0;
  right: 0;
}
</style>
